import React, { FC } from 'react';

import styled from 'styled-components';

import { useModalManager } from '@hooks/useModal';
import { ModalConfig, ModalContext } from '@contexts/modalContext';
import { ConditionalRendering } from '@common-components/conditional-rendering';
import { BREAK_POINTS } from '@constants/common/breakpoints';

interface WithModalWrapperProps {
    modalConfig?: ModalConfig;
}

const ModalWrapper = styled.div`
    display: contents;
    .gi-modal__body {
        margin-bottom: 30px;
    }

    @media (max-width: ${BREAK_POINTS.sm}) {
        .gi-modal__body {
            max-height: 75%;
        }
    }
`;

// HOC to provide modal context
const withModalWrapper = <P extends object>(
    WrappedComponent: React.ComponentType<P>,
    modalConfig: ModalConfig = {},
): FC<P & WithModalWrapperProps> => {
    const ComponentWithModalWrapper: FC<P & WithModalWrapperProps> = props => {
        const { openModals, openModal, closeModal, closeAllModals } = useModalManager();

        const isModalOpen = (modalId: string) => openModals.some(modal => modal.id === modalId);

        const topMostModal = openModals[openModals.length - 1];

        const ActiveModal = modalConfig?.[topMostModal?.id]?.modalComponent;

        return (
            <ModalContext.Provider
                value={{
                    openModal,
                    closeModal,
                    isModalOpen,
                    modalConfig,
                    closeAllModals,
                }}
            >
                <div id="modal-wrapper">
                    <ConditionalRendering isTrue={!!ActiveModal} key={topMostModal?.id}>
                        <ModalWrapper>
                            <ActiveModal
                                {...{
                                    openModal,
                                    closeModal,
                                    isModalOpen,
                                    modalConfig,
                                    closeAllModals,
                                    ...topMostModal?.props,
                                }}
                                className="modal-dialog"
                            />
                        </ModalWrapper>
                    </ConditionalRendering>

                    <WrappedComponent {...(props as P)} />
                </div>
            </ModalContext.Provider>
        );
    };

    return ComponentWithModalWrapper;
};

export default withModalWrapper;
